/**
 * Wire shape of /api/v1/audit rows.
 *
 * Distinct from `audit.ts` (which only described the legacy domain
 * audit trail used by leaves). This is the timeline entry that the
 * HR-side Audit Log page renders.
 */
import { z } from 'zod'
import { auditAiProvenanceSchema } from './audit.js'

/** One field-level change computed at write time. `before`/`after` are the
 *  raw JSON values on either side (null when the field was absent). */
export const auditChangeEntrySchema = z.object({
  field: z.string(),
  before: z.unknown().nullable().optional(),
  after: z.unknown().nullable().optional(),
})
export type AuditChangeEntry = z.infer<typeof auditChangeEntrySchema>

export const auditEntrySchema = z.object({
  id: z.guid(),
  actorEmployeeId: z.guid().nullable(),
  actorName: z.string().nullable(),
  actorRole: z.string().nullable(),
  action: z.string(),
  // Humanized view of `action`, filled by the api from the audit registry.
  // `category` is a stable kebab-case bucket ("leave-application"); `label`
  // is the plain-English past-tense event ("Leave approved"). Older api
  // builds may omit these, so default them for forward/backward safety.
  category: z.string().default(''),
  label: z.string().default(''),
  subjectType: z.string(),
  subjectId: z.guid().nullable(),
  // Resolved display name of the subject ("Ben Tan · annual · 12–14 Aug").
  // Null when the api could not resolve it.
  subjectLabel: z.string().nullable().default(null),
  // Specific chip label ("Team" / "Department" / "Function") when the broad
  // category is too coarse; null → UI shows the category chip.
  subjectKind: z.string().nullable().default(null),
  before: z.unknown().nullable(),
  after: z.unknown().nullable(),
  // Field-level diff ([{field, before, after}]); [] when nothing changed.
  changeSummary: z.array(auditChangeEntrySchema).default([]),
  metadata: z.unknown().nullable(),
  source: z.string(),
  createdAt: z.string().datetime(),
  // NEW (Slice 16b): populated when the audit row carries AI provenance.
  // Null for all pre-Slice-16a rows.
  aiProvenance: auditAiProvenanceSchema.nullable().optional(),
})

export type AuditEntry = z.infer<typeof auditEntrySchema>

/**
 * One row of the grouped-by-object audit view (`GET /api/v1/audit/subjects`):
 * every audit event for a single object collapsed into one entry, carrying the
 * latest event's label/actor/time plus a change count. Expanding it fetches
 * that object's events via the list route's subject filter.
 */
export const auditSubjectGroupSchema = z.object({
  subjectType: z.string(),
  subjectId: z.guid().nullable(),
  subjectLabel: z.string().nullable().default(null),
  subjectKind: z.string().nullable().default(null),
  category: z.string().default(''),
  latestAction: z.string(),
  latestLabel: z.string().default(''),
  latestActorName: z.string().nullable().default(null),
  latestActorRole: z.string().nullable().default(null),
  latestSource: z.string(),
  changeCount: z.number(),
  firstChangedAt: z.string().datetime(),
  lastChangedAt: z.string().datetime(),
})

export type AuditSubjectGroup = z.infer<typeof auditSubjectGroupSchema>
